/**
 * Writer workflow state management.
 * Manages form state, validation status, and recent readers for quick access.
 */

import { setState, getState, batchUpdate } from './store.js';

// localStorage key for persisting recent readers
const RECENT_READERS_KEY = 'dex_recent_readers';

// Maximum number of recent readers to keep
const MAX_RECENT_READERS = 5;

/**
 * Initialize writer state in the store.
 * Call this when the writer workflow is entered.
 */
export function initWriterState() {
  batchUpdate({
    'writer.readerId': '',
    'writer.readerValidated': false,
    'writer.readerPublicKey': null,
    'writer.readerError': null,
    'writer.recentReaders': [],
    'writer.contentTab': 'file',
    'writer.file': null,
    'writer.fileName': '',
    'writer.textContent': '',
    'writer.workflowId': '',
    'writer.sampleId': '',
    'writer.stageId': '',
    'writer.status': 'idle',
    'writer.error': null
  });

  // Restore recent readers from localStorage
  loadRecentReaders();
}

/**
 * Set a single writer field value.
 *
 * @param {string} field - Field name (without 'writer.' prefix)
 * @param {*} value - Value to set
 */
export function setWriterField(field, value) {
  setState(`writer.${field}`, value);
}

/**
 * Set writer workflow status with optional error.
 *
 * @param {'idle'|'validating'|'encrypting'|'uploading'} status - Current status
 * @param {string|null} error - Error message, or null to clear
 */
export function setWriterStatus(status, error = null) {
  batchUpdate({
    'writer.status': status,
    'writer.error': error
  });
}

/**
 * Reset the writer form for a new submission.
 * Keeps reader info if already validated for quick re-send.
 * Keeps recentReaders.
 */
export function resetWriterForm() {
  const state = getState();
  const keepReader = state.writer?.readerValidated;

  const updates = {
    'writer.file': null,
    'writer.fileName': '',
    'writer.textContent': '',
    'writer.workflowId': '',
    'writer.sampleId': '',
    'writer.stageId': '',
    'writer.status': 'idle',
    'writer.error': null,
    'writer.contentTab': 'file'
  };

  // Reset reader if not validated (keep validated reader for quick re-send)
  if (!keepReader) {
    updates['writer.readerId'] = '';
    updates['writer.readerValidated'] = false;
    updates['writer.readerPublicKey'] = null;
    updates['writer.readerError'] = null;
  }

  batchUpdate(updates);
}

/**
 * Add a reader to the recent readers list.
 * Persists to localStorage for cross-session access.
 *
 * @param {string} readerId - Reader's party ID
 * @param {string} publicKey - Reader's public key (base64)
 */
export function addRecentReader(readerId) {
  const state = getState();
  const current = state.writer?.recentReaders || [];

  // Create new entry (only store ID — public key is always fetched fresh)
  const newEntry = {
    id: readerId,
    lastUsed: Date.now()
  };

  // Remove any existing entry for this reader
  const filtered = current.filter(r => r.id !== readerId);

  // Add new entry at front, limit to max
  const updated = [newEntry, ...filtered].slice(0, MAX_RECENT_READERS);

  // Update state
  setState('writer.recentReaders', updated);

  // Persist to localStorage
  try {
    localStorage.setItem(RECENT_READERS_KEY, JSON.stringify(updated));
  } catch (e) {
    // Silently ignore storage errors (private browsing, quota exceeded, etc.)
  }
}

/**
 * Load recent readers from localStorage into state.
 * Called during initWriterState().
 */
export function loadRecentReaders() {
  try {
    const stored = localStorage.getItem(RECENT_READERS_KEY);
    if (stored) {
      const readers = JSON.parse(stored);
      if (Array.isArray(readers)) {
        setState('writer.recentReaders', readers.slice(0, MAX_RECENT_READERS));
      }
    }
  } catch (e) {
    // Silently ignore parse errors or storage issues
  }
}

/**
 * Clear all writer state (for cleanup on logout or role switch).
 */
export function clearWriterState() {
  batchUpdate({
    'writer.readerId': '',
    'writer.readerValidated': false,
    'writer.readerPublicKey': null,
    'writer.readerError': null,
    'writer.recentReaders': [],
    'writer.contentTab': 'file',
    'writer.file': null,
    'writer.fileName': '',
    'writer.fileError': null,
    'writer.textContent': '',
    'writer.textError': null,
    'writer.workflowId': '',
    'writer.sampleId': '',
    'writer.stageId': '',
    'writer.status': 'idle',
    'writer.error': null
  });
}
